/**
 * `AssistantsSection` — ADR-0262: how merchants use Sill from their
 * own assistant (Claude, Claude Code, ChatGPT) over the MCP server.
 *
 * Blocks, top to bottom:
 *   1. KPI tiles — connected accounts, live connections, active
 *      connections in the window, tool calls, error rate (refusals
 *      shown beside it, never inside it), silent connections, time to
 *      first call.
 *   2. Calls per day (tool calls + errors + active connections).
 *   3. By assistant — live / active connections, calls, error rate.
 *   4. Tools — calls, error rate, average and worst-day p95, top error.
 *   5. Sign-in funnel — authorise → consent → token → refresh, plus
 *      rejections, revocations by reason and write tools called
 *      without the permission.
 *
 * Every number comes from `/internal/admin/metrics/assistants`; a
 * dash means no data. Aggregate-only: no account or user appears.
 */

import type {
  AdminAssistantsClientRow,
  AdminAssistantsFunnel,
  AdminAssistantsToolRow,
  AdminMetricsAssistants,
  AssistantClientKind,
} from '@sill/types';

import { Card } from '../../../components/atoms/Card.js';
import {
  formatBucketDateUTC,
  formatCount,
  formatDurationSeconds,
  formatPct,
} from '../../../lib/format.js';
import { useC } from '../../../lib/theme.js';
import { KpiTile, SectionHeader } from '../KpiTile.js';
import { LineAreaChart } from '../LineAreaChart.js';

const monoFont = "'JetBrains Mono', monospace";
const sansFont = "'Geist', sans-serif";

export type AssistantsSectionState =
  | { kind: 'loading' }
  | { kind: 'error'; message: string }
  | { kind: 'ready'; assistants: AdminMetricsAssistants };

export interface AssistantsSectionProps {
  state: AssistantsSectionState;
}

const TILE_MIN_HEIGHT = 112;

export const CLIENT_KIND_LABEL: Record<AssistantClientKind, string> = {
  claude: 'Claude (web, desktop, mobile)',
  claude_code: 'Claude Code',
  chatgpt: 'ChatGPT',
  other: 'Other',
};

const FUNNEL_STEPS: ReadonlyArray<{ key: keyof AdminAssistantsFunnel; label: string }> = [
  { key: 'authorize_started', label: 'Sign-in started' },
  { key: 'consent_approved', label: 'Consent approved' },
  { key: 'consent_denied', label: 'Consent denied' },
  { key: 'token_issued', label: 'Tokens issued' },
  { key: 'token_refreshed', label: 'Tokens refreshed' },
  { key: 'refresh_reuse_detected', label: 'Refresh reuse detected' },
  { key: 'call_rejected_401', label: 'Calls rejected (bad or expired token)' },
  { key: 'scope_insufficient', label: 'Calls without permission' },
];

/** Error-rate colour: lime under 2 %, amber under 10 %, coral above. */
export function errorRateTone(rate: number | null): 'lime' | 'amber' | 'coral' | 'muted' {
  if (rate === null) return 'muted';
  if (rate < 0.02) return 'lime';
  if (rate < 0.1) return 'amber';
  return 'coral';
}

export function AssistantsSection({ state }: AssistantsSectionProps): JSX.Element {
  const C = useC();
  const header = (
    <SectionHeader
      kicker="Assistants"
      title="Sill from Claude and ChatGPT"
      caption="Connections, calls, errors and the sign-in funnel for the assistant MCP server. Errors are calls that ran and failed; refusals (no permission, a cap, bad arguments) are counted beside them. Aggregate only."
    />
  );

  if (state.kind === 'loading') {
    return (
      <section>
        {header}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {Array.from({ length: 8 }, (_, i) => (
            <KpiTile key={`loading-${i}`} kicker="…" value="…" variant="loading" minHeight={TILE_MIN_HEIGHT} />
          ))}
        </div>
      </section>
    );
  }

  if (state.kind === 'error') {
    return (
      <section>
        {header}
        <div
          role="alert"
          style={{ fontFamily: monoFont, fontSize: 11, color: C.coral, border: `1px solid ${C.border}`, padding: 12 }}
        >
          Could not load assistant analytics: {state.message}
        </div>
      </section>
    );
  }

  const a = state.assistants;
  const s = a.snapshot;
  const xLabels = a.series.map((p) => formatBucketDateUTC(p.bucket_start));
  const tone = errorRateTone(s.error_rate);
  const window_days = Math.round((new Date(a.window.to).getTime() - new Date(a.window.from).getTime()) / 86_400_000);
  const raw_bounded = window_days > s.raw_retention_days;
  const raw_note = raw_bounded ? ` Last ${s.raw_retention_days} days only.` : '';
  const toneColor = tone === 'lime' ? C.lime : tone === 'amber' ? C.amber : tone === 'coral' ? C.coral : C.muted;

  return (
    <section data-testid="assistants-section">
      {header}

      {/* 1. KPI tiles */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        <KpiTile kicker="Connected accounts" value={formatCount(s.accounts_connected)} sub={`${formatCount(s.live_grants)} live connections`} minHeight={TILE_MIN_HEIGHT} />
        <KpiTile kicker="Active in window" value={formatCount(s.active_grants)} sub={`${formatCount(s.new_grants)} new · ${formatCount(s.revoked_grants)} revoked`} footnote={raw_bounded ? `Connections that made a tool call.${raw_note}` : undefined} minHeight={TILE_MIN_HEIGHT} />
        <KpiTile kicker="Tool calls" value={formatCount(s.tool_calls)} sub={`${formatCount(s.requests)} requests incl. handshakes`} minHeight={TILE_MIN_HEIGHT} />
        <KpiTile
          kicker="Error rate"
          value={<span style={{ color: toneColor }}>{formatPct(s.error_rate)}</span>}
          sub={`${formatCount(s.tool_errors)} errors · ${formatCount(s.tool_refusals)} refused`}
          minHeight={TILE_MIN_HEIGHT}
        />
        <KpiTile kicker="Silent connections" value={formatCount(s.silent_grants)} footnote={`Live connections from the last ${s.raw_retention_days} days that have never made a tool call.`} minHeight={TILE_MIN_HEIGHT} />
        <KpiTile kicker="With write permission" value={formatCount(s.live_grants_with_write)} footnote="Live connections holding a write permission: blocking, readiness runs or account switching." minHeight={TILE_MIN_HEIGHT} />
        <KpiTile
          kicker="Time to first call"
          value={s.median_time_to_first_call_seconds === null ? '—' : formatDurationSeconds(s.median_time_to_first_call_seconds)}
          footnote={`Median, token issued → first tool call, connections created in the window.${raw_note}`}
          minHeight={TILE_MIN_HEIGHT}
        />
        <KpiTile kicker="Refresh reuse" value={formatCount(a.funnel.refresh_reuse_detected)} footnote="Should be zero. Any value is a security signal." minHeight={TILE_MIN_HEIGHT} />
      </div>

      {/* 2. Calls per day */}
      <Card className="mb-6" style={{ padding: 16 }}>
        <div style={{ fontFamily: monoFont, fontSize: 10, color: C.muted, letterSpacing: 2, textTransform: 'uppercase', marginBottom: 8 }}>
          Calls per day
        </div>
        <LineAreaChart<AdminMetricsAssistants['series'][number]>
          data={a.series}
          xLabels={xLabels}
          ariaLabel="Assistant tool calls, errors and active connections per day"
          series={[
            { key: 'tool_calls', label: 'Tool calls', color: '#D9FF3F', accessor: (p) => p.tool_calls },
            { key: 'tool_errors', label: 'Errors', color: '#FF6B47', accessor: (p) => p.tool_errors, fill: false },
            { key: 'active_grants', label: 'Active connections', color: '#7FB8FF', accessor: (p) => p.active_grants, fill: false },
          ]}
          emptyState={<span style={{ fontFamily: sansFont, fontSize: 12, color: C.muted }}>No assistant calls in this window.</span>}
        />
      </Card>

      {/* 3. By assistant */}
      <Card className="mb-6" style={{ padding: 0, overflow: 'hidden' }}>
        <Table
          caption="By assistant"
          head={['Assistant', 'Live', 'Active', 'Tool calls', 'Errors', 'Error rate']}
          rows={a.by_client.map((r: AdminAssistantsClientRow) => [
            CLIENT_KIND_LABEL[r.client_kind],
            formatCount(r.live_grants),
            formatCount(r.active_grants),
            formatCount(r.tool_calls),
            formatCount(r.tool_errors),
            r.tool_calls === 0 ? '—' : formatPct(r.tool_errors / r.tool_calls),
          ])}
          empty="No connections yet."
        />
      </Card>

      {/* 4. Tools */}
      <Card className="mb-6" style={{ padding: 0, overflow: 'hidden' }}>
        <Table
          caption="Tools"
          head={['Tool', 'Kind', 'Calls', 'Errors', 'Error rate', 'Avg ms (ok)', 'Worst-day p95 ms (ok)', 'Top error']}
          rows={a.tools.map((r: AdminAssistantsToolRow) => [
            r.tool,
            r.kind,
            formatCount(r.calls),
            formatCount(r.errors),
            formatPct(r.error_rate),
            formatCount(r.avg_ms),
            formatCount(r.p95_ms_worst_day),
            r.top_error ?? '—',
          ])}
          empty="No tool calls in this window."
        />
      </Card>

      {/* 5. Funnel + revocations + scope misses */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <Card style={{ padding: 0, overflow: 'hidden' }}>
          <Table
            caption="Sign-in funnel"
            head={['Step', 'Count']}
            rows={FUNNEL_STEPS.map((f) => [f.label, formatCount(a.funnel[f.key])])}
            empty="No sign-in activity."
          />
        </Card>
        <Card style={{ padding: 0, overflow: 'hidden' }}>
          <Table
            caption="Revoked, by reason"
            head={['Reason', 'Count']}
            rows={a.revocations.map((r) => [r.reason, formatCount(r.count)])}
            empty="No revocations in this window."
          />
        </Card>
        <Card style={{ padding: 0, overflow: 'hidden' }}>
          <Table
            caption="Write tools called without permission"
            head={['Tool', 'Count']}
            rows={a.scope_misses.map((r) => [r.tool, formatCount(r.count)])}
            empty="None. Nobody hit a write tool they had not allowed."
          />
        </Card>
      </div>
    </section>
  );
}

function Table({
  caption,
  head,
  rows,
  empty,
}: {
  caption: string;
  head: ReadonlyArray<string>;
  rows: ReadonlyArray<ReadonlyArray<string>>;
  empty: string;
}): JSX.Element {
  const C = useC();
  return (
    <div>
      <div
        style={{
          fontFamily: monoFont, fontSize: 10, color: C.muted, letterSpacing: 2, textTransform: 'uppercase',
          padding: '12px 16px 8px', borderBottom: `1px solid ${C.border}`,
        }}
      >
        {caption}
      </div>
      {rows.length === 0 ? (
        <div style={{ fontFamily: sansFont, fontSize: 12, color: C.muted, padding: 16 }}>{empty}</div>
      ) : (
        <div className="overflow-x-auto">
          <table style={{ width: '100%', borderCollapse: 'collapse', fontFamily: monoFont, fontSize: 11 }}>
            <thead>
              <tr>
                {head.map((h, i) => (
                  <th
                    key={h}
                    scope="col"
                    style={{
                      textAlign: i === 0 ? 'left' : 'right', padding: '8px 16px', color: C.muted, fontWeight: 400,
                      letterSpacing: 0.5, borderBottom: `1px solid ${C.border}`, whiteSpace: 'nowrap',
                    }}
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((r, ri) => (
                <tr key={ri}>
                  {r.map((cell, ci) => (
                    <td
                      key={ci}
                      style={{
                        textAlign: ci === 0 ? 'left' : 'right', padding: '8px 16px', color: ci === 0 ? C.text : C.muted,
                        borderBottom: `1px solid ${C.border}`, whiteSpace: 'nowrap',
                      }}
                    >
                      {cell}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
